import {FC, useState} from 'react'
import {useQuery} from 'react-query'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'
import {KTCard, KTIcon} from '../../../../_metronic/helpers'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core'
import {LoadingListLoading} from '../../../../utils/loading/loadingListLoading'
import {formatMonetaryToBr} from '../../../../utils/formatMonetaryToBr'
import {StarChangesFilters, StarSnapshot} from './core/_models'
import {exportStarChanges, getStarChanges} from './core/_requests'

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Troca de Estrelas',
    path: '/reports/reportStarChanges/list',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

// Padrão: do início do ano até o mês atual.
const DEFAULT_FILTERS: StarChangesFilters = {
  from: dayjs().startOf('year').format('YYYY-MM'),
  to: dayjs().format('YYYY-MM'),
  only_existing: false,
}

const formatMonth = (month: string) => dayjs(`${month}-01`).format('MM/YYYY')

const StarCell: FC<{snapshot: StarSnapshot}> = ({snapshot}) => {
  if (snapshot.stars === null) {
    return <span className='text-muted'>—</span>
  }

  return (
    <div className='d-flex flex-column'>
      <span className='fw-bold'>
        {snapshot.stars} <KTIcon iconName='star' className='fs-5 text-warning' />
        {snapshot.description && (
          <span className='text-muted fw-normal ms-2'>{snapshot.description}</span>
        )}
      </span>
      <span className='text-muted fs-7'>{formatMonetaryToBr(snapshot.total ?? 0)}</span>
    </div>
  )
}

const TrendBadge: FC<{from: number | null; to: number | null}> = ({from, to}) => {
  if (from === null) {
    return <span className='badge badge-light-info'>Novo</span>
  }
  if (to === null || to < from) {
    return <span className='badge badge-light-danger'>Caiu</span>
  }

  return <span className='badge badge-light-success'>Subiu</span>
}

const ReportStarChangesPage = () => {
  const [draft, setDraft] = useState<StarChangesFilters>(DEFAULT_FILTERS)
  const [filters, setFilters] = useState<StarChangesFilters>(DEFAULT_FILTERS)

  const isInvalid = !draft.from || !draft.to || draft.to < draft.from

  const {data, isFetching} = useQuery(['star-changes', filters], () => getStarChanges(filters), {
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const rows = data?.data ?? []

  const downloadFile = async () => {
    try {
      const result = await exportStarChanges(filters)
      Swal.fire({
        icon: 'info',
        title: 'Relatório em processamento',
        text: result.message,
        confirmButtonColor: '#d33',
      })
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message,
        timer: 2000,
      })
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>Troca de Estrelas</PageTitle>

      <div className='card mb-2 mb-xl-2'>
        <div className='card-body py-5'>
          <div className='d-flex flex-wrap align-items-end gap-4'>
            <div>
              <label className='form-label fs-6 fw-bold'>Mês inicial:</label>
              <input
                type='month'
                className='form-control form-control-solid'
                value={draft.from}
                onChange={(e) => setDraft((current) => ({...current, from: e.target.value}))}
              />
            </div>
            <div>
              <label className='form-label fs-6 fw-bold'>Mês final:</label>
              <input
                type='month'
                className={`form-control form-control-solid ${isInvalid ? 'is-invalid' : ''}`}
                value={draft.to}
                onChange={(e) => setDraft((current) => ({...current, to: e.target.value}))}
              />
            </div>
            <div className='form-check form-check-custom form-check-solid mb-3'>
              <input
                id='only_existing'
                type='checkbox'
                className='form-check-input'
                checked={!!draft.only_existing}
                onChange={(e) =>
                  setDraft((current) => ({...current, only_existing: e.target.checked}))
                }
              />
              <label className='form-check-label' htmlFor='only_existing'>
                Ignorar vendedores novos no período
              </label>
            </div>
            <div className='ms-auto d-flex'>
              <button
                type='button'
                disabled={isFetching || isInvalid}
                className='btn btn-danger fw-bold me-3'
                onClick={() => setFilters(draft)}
              >
                <KTIcon iconName='filter' className='fs-2' />
                Filtrar
              </button>
              <button
                type='button'
                disabled={isFetching}
                className='btn btn-light-danger'
                onClick={() => void downloadFile()}
              >
                <KTIcon iconName='exit-up' className='fs-2' />
                Export
              </button>
            </div>
          </div>
          {isInvalid && (
            <div className='text-danger fs-7 mt-2'>
              O mês final não pode ser anterior ao inicial.
            </div>
          )}
        </div>
      </div>

      <KTCard>
        <div className='p-4 position-relative' style={{minHeight: '200px'}}>
          {data?.period && (
            <div className='text-muted mb-4'>
              Comparando o acumulado de vendas até o fim de {formatMonth(data.period.from)} com o
              acumulado até o fim de {formatMonth(data.period.to)}.{' '}
              <span className='fw-bold'>{rows.length} vendedor(es) trocaram de estrela.</span>
            </div>
          )}
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-4'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-200px'>Vendedor</th>
                  <th className='min-w-150px'>
                    {data?.period ? formatMonth(data.period.from) : 'De'}
                  </th>
                  <th className='min-w-150px'>
                    {data?.period ? formatMonth(data.period.to) : 'Para'}
                  </th>
                  <th className='min-w-100px text-end'>Movimento</th>
                </tr>
              </thead>
              <tbody className='text-gray-600'>
                {rows.map((row) => (
                  <tr key={row.sellerId}>
                    <td className='fw-bold text-gray-800'>
                      {row.sellerName ?? `#${row.sellerId}`}
                    </td>
                    <td>
                      <StarCell snapshot={row.from} />
                    </td>
                    <td>
                      <StarCell snapshot={row.to} />
                    </td>
                    <td className='text-end'>
                      <TrendBadge from={row.from.stars} to={row.to.stars} />
                    </td>
                  </tr>
                ))}
                {!rows.length && !isFetching && (
                  <tr>
                    <td colSpan={4} className='text-center text-muted py-10'>
                      Nenhum vendedor trocou de estrela no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {isFetching && <LoadingListLoading title='Carregando' />}
        </div>
      </KTCard>
    </>
  )
}

export default ReportStarChangesPage
